import React, { createContext, useContext, useState, useEffect } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import { getAppSetting, setAppSetting } from '../services/database';
import { useAuth } from '../services/AuthContext';

const MapSettingsContext = createContext();

// Storage keys
const VIEWPORT_BUFFER_KEY = '@map_viewport_buffer';
const USER_OVERRIDE_KEY = '@map_viewport_buffer_override'; // Flag to indicate user has set their own preference

// Default settings (fallback if database setting not found)
const DEFAULT_VIEWPORT_BUFFER = 0.5; // 50% buffer - shows markers within 150% of visible area
const DEFAULT_VIEWPORT_BUFFER_MOBILE = 0.2; // Mobile default: minimal (~20–30 markers) for better performance

// Buffer presets for admin slider
export const BUFFER_PRESETS = {
  MINIMAL: { value: 0.2, label: 'Minimal', description: 'Shows ~20-30 markers' },
  NORMAL: { value: 0.5, label: 'Normal', description: 'Shows ~40-60 markers' },
  EXTENDED: { value: 1.0, label: 'Extended', description: 'Shows ~80-100 markers' },
  MAXIMUM: { value: 2.0, label: 'Maximum', description: 'Shows ~150+ markers' },
  ALL: { value: 999, label: 'All Markers', description: 'No viewport filtering' },
};

export const useMapSettings = () => {
  const context = useContext(MapSettingsContext);
  if (!context) {
    throw new Error('useMapSettings must be used within a MapSettingsProvider');
  }
  return context;
};

export const MapSettingsProvider = ({ children }) => {
  const { clerkUserId } = useAuth();
  const [viewportBuffer, setViewportBufferState] = useState(DEFAULT_VIEWPORT_BUFFER);
  const [globalDefault, setGlobalDefaultState] = useState(DEFAULT_VIEWPORT_BUFFER);
  const [globalDefaultMobile, setGlobalDefaultMobileState] = useState(DEFAULT_VIEWPORT_BUFFER_MOBILE);
  const [hasUserOverride, setHasUserOverrideState] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load global default and user override on mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      // 1. Load global defaults from database (desktop and mobile)
      let globalBuffer = DEFAULT_VIEWPORT_BUFFER;
      let mobileBuffer = DEFAULT_VIEWPORT_BUFFER_MOBILE;
      try {
        const dbValue = await getAppSetting('map_viewport_buffer');
        if (dbValue !== null) {
          globalBuffer = typeof dbValue === 'string' ? parseFloat(dbValue) : dbValue;
          setGlobalDefaultState(globalBuffer);
        }
      } catch (error) {
        console.warn('Could not load global default from database:', error);
      }
      try {
        const mobileValue = await getAppSetting('map_viewport_buffer_mobile');
        if (mobileValue !== null) {
          mobileBuffer = typeof mobileValue === 'string' ? parseFloat(mobileValue) : mobileValue;
          setGlobalDefaultMobileState(mobileBuffer);
        }
      } catch (error) {
        console.warn('Could not load mobile default from database:', error);
      }

      // On mobile, use the mobile default as the effective global default
      const isMobile = Platform.OS !== 'web';
      const effectiveGlobal = isMobile ? mobileBuffer : globalBuffer;

      // 2. Check if user has set their own override
      let userOverride = null;
      let hasOverride = false;
      
      if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
        hasOverride = localStorage.getItem(USER_OVERRIDE_KEY) === 'true';
        if (hasOverride) {
          const saved = localStorage.getItem(VIEWPORT_BUFFER_KEY);
          if (saved !== null) {
            userOverride = parseFloat(saved);
          }
        }
      } else {
        hasOverride = await AsyncStorage.getItem(USER_OVERRIDE_KEY) === 'true';
        if (hasOverride) {
          const saved = await AsyncStorage.getItem(VIEWPORT_BUFFER_KEY);
          if (saved !== null) {
            userOverride = parseFloat(saved);
          }
        }
      }

      setHasUserOverrideState(hasOverride);
      
      // 3. Use user override if exists, otherwise use effective global default (mobile vs desktop)
      const finalValue = hasOverride && userOverride !== null ? userOverride : effectiveGlobal;
      setViewportBufferState(finalValue);
      
      console.log('🗺️ Map settings loaded:', {
        globalDefault: globalBuffer,
        globalDefaultMobile: mobileBuffer,
        effectiveGlobal,
        userOverride,
        hasOverride,
        finalValue,
      });
    } catch (error) {
      console.error('Error loading map settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Set global default for mobile only (admin). Uses same presets as viewport buffer.
  const setMobileDefault = async (value, clerkIdOverride = null) => {
    const cid = clerkIdOverride || clerkUserId;
    if (!cid) throw new Error('Sign-in required to update global default. Try refreshing.');
    const numValue = typeof value === 'number' && isFinite(value) ? value : parseFloat(value);
    if (numValue !== numValue) throw new Error('Invalid value');
    await setAppSetting('map_viewport_buffer_mobile', numValue, null, cid);
    setGlobalDefaultMobileState(numValue);
    if (Platform.OS !== 'web') {
      if (!hasUserOverride) setViewportBufferState(numValue);
    }
    console.log('🗺️ Global default (mobile) viewport buffer changed to:', numValue);
  };

  // Set viewport buffer (user override)
  // When isGlobalDefault: pass clerkIdOverride from caller (e.g. clerkUserId || appUser?.clerkId) or use context clerkUserId
  const setViewportBuffer = async (value, isGlobalDefault = false, clerkIdOverride = null) => {
    try {
      setViewportBufferState(value);
      
      if (isGlobalDefault) {
        const cid = clerkIdOverride || clerkUserId;
        if (!cid) {
          throw new Error('Sign-in required to update global default. Try refreshing.');
        }
        // Coerce to number for JSONB; RPC validates admin via users.clerk_id
        const numValue = typeof value === 'number' && isFinite(value) ? value : parseFloat(value);
        if (numValue !== numValue) throw new Error('Invalid value');
        await setAppSetting('map_viewport_buffer', numValue, null, cid);
        setGlobalDefaultState(numValue);
        console.log('🗺️ Global default viewport buffer changed to:', numValue);
      } else {
        // User setting their own override
        if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
          localStorage.setItem(VIEWPORT_BUFFER_KEY, String(value));
          localStorage.setItem(USER_OVERRIDE_KEY, 'true');
        } else {
          await AsyncStorage.setItem(VIEWPORT_BUFFER_KEY, String(value));
          await AsyncStorage.setItem(USER_OVERRIDE_KEY, 'true');
        }
        setHasUserOverrideState(true);
        console.log('🗺️ User viewport buffer override changed to:', value);
      }
    } catch (error) {
      console.error('Error saving viewport buffer:', error);
      throw error;
    }
  };

  // Clear user override (use effective global default: mobile vs desktop)
  const clearUserOverride = async () => {
    try {
      if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
        localStorage.removeItem(VIEWPORT_BUFFER_KEY);
        localStorage.removeItem(USER_OVERRIDE_KEY);
      } else {
        await AsyncStorage.removeItem(VIEWPORT_BUFFER_KEY);
        await AsyncStorage.removeItem(USER_OVERRIDE_KEY);
      }
      setHasUserOverrideState(false);
      const effectiveDefault = Platform.OS !== 'web' ? globalDefaultMobile : globalDefault;
      setViewportBufferState(effectiveDefault);
      console.log('🗺️ User override cleared, using global default:', effectiveDefault);
    } catch (error) {
      console.error('Error clearing user override:', error);
    }
  };

  // Get current preset based on buffer value
  const getCurrentPreset = () => {
    const presets = Object.entries(BUFFER_PRESETS);
    for (const [key, preset] of presets) {
      if (Math.abs(preset.value - viewportBuffer) < 0.01) {
        return key;
      }
    }
    return 'CUSTOM';
  };

  return (
    <MapSettingsContext.Provider
    value={{
        viewportBuffer,
        setViewportBuffer,
        setMobileDefault,
        clearUserOverride,
        getCurrentPreset,
        isLoading,
        BUFFER_PRESETS,
        globalDefault,
        globalDefaultMobile,
        hasUserOverride,
      }}
    >
      {children}
    </MapSettingsContext.Provider>
  );
};

